import { useMemo, useState } from "react";
import "./trurecipe-pages.scss";
import "./digital-twin.scss";

type DtTab = "task" | "batch" | "spc" | "analytics";

type TwinMetricParam = {
  kind: "metric";
  name: string;
  critical?: boolean;
  should: string;
  actual: string;
  inSpec: boolean;
  barPct: number;
  range: string;
  source: string;
};

type TwinBinaryParam = {
  kind: "binary";
  name: string;
  should: string;
  actual: string;
  inSpec: boolean;
  corrective?: string;
};

type TwinParam = TwinMetricParam | TwinBinaryParam;

type TwinTask = {
  id: string;
  title: string;
  recipeLine: string;
  note?: string;
  params: TwinParam[];
};

const PART_OPTIONS = [{ id: "crank", label: "Crankshaft Housing" }];

const BATCH_ROWS = [
  { id: "LOT-001", qty: 50, pass: 48, fail: 2, yield: 96, status: "released" as const },
  { id: "LOT-002", qty: 50, pass: 45, fail: 5, yield: 90, status: "review" as const },
  { id: "LOT-003", qty: 25, pass: 25, fail: 0, yield: 100, status: "released" as const },
  { id: "LOT-004", qty: 40, pass: 38, fail: 2, yield: 95, status: "released" as const },
];

const TECH_ITEMS: { name: string; on: boolean }[] = [
  { name: "CNC Machining", on: true },
  { name: "Sheet Metal", on: true },
  { name: "Injection Molding", on: true },
  { name: "Die Casting", on: false },
  { name: "3D Printing", on: false },
  { name: "Welding", on: false },
  { name: "Forging", on: false },
  { name: "Heat Treat", on: false },
  { name: "Plating", on: false },
  { name: "Assembly", on: false },
];

const DEV_ANALYSIS = [
  { recipe: "jt1", status: "Verified", params: 7, oot: "All within spec" },
  { recipe: "jt2", status: "Deviation", params: 6, oot: "All within spec" },
  { recipe: "jt22", status: "In Monitoring", params: 7, oot: "All within spec" },
  { recipe: "jt6", status: "Verified", params: 6, oot: "All within spec" },
];

const TASKS: TwinTask[] = [
  {
    id: "t1",
    title: "Rough Turn OD & Bore",
    recipeLine: "Recipe: Verified · 7 readings · Set by Shanmugam K",
    note: "Standard aluminum turning recipe applied.",
    params: [
      {
        kind: "metric",
        name: "Spindle Speed",
        critical: true,
        should: "1800 RPM",
        actual: "1798 RPM",
        inSpec: true,
        barPct: 48,
        range: "Range: —",
        source: "IoT: —",
      },
      {
        kind: "metric",
        name: "Feed Rate",
        critical: true,
        should: "0.15 mm/rev",
        actual: "0.149 mm/rev",
        inSpec: true,
        barPct: 45,
        range: "Range: —",
        source: "IoT: —",
      },
      {
        kind: "metric",
        name: "Depth of Cut",
        should: "1.2 mm",
        actual: "1.2 mm",
        inSpec: true,
        barPct: 50,
        range: "Range: —",
        source: "Manual: —",
      },
      {
        kind: "metric",
        name: "Coolant Pressure",
        should: "3.2 bar",
        actual: "3.2 bar",
        inSpec: true,
        barPct: 55,
        range: "Range: —",
        source: "IoT: —",
      },
      {
        kind: "metric",
        name: "Surface Finish Target",
        critical: true,
        should: "Ra 1.6",
        actual: "Ra 1.5",
        inSpec: true,
        barPct: 62,
        range: "Range: —",
        source: "Manual: —",
      },
      {
        kind: "metric",
        name: "Tool Nose Radius",
        should: "0.4 mm",
        actual: "0.4 mm",
        inSpec: true,
        barPct: 50,
        range: "Range: —",
        source: "Manual: —",
      },
      {
        kind: "binary",
        name: "Coolant On",
        should: "OFF",
        actual: "ON",
        inSpec: false,
        corrective: "Check coolant pump pressure, verify nozzle alignment.",
      },
    ],
  },
  {
    id: "t2",
    title: "Finish Turn to Spec",
    recipeLine: "Recipe: Deviation · 6 readings · Set by Shanmugam K",
    note: "Spindle bearing vibration detected — surface finish degraded",
    params: [
      {
        kind: "metric",
        name: "Spindle Speed",
        critical: true,
        should: "2200 RPM",
        actual: "2195 RPM",
        inSpec: true,
        barPct: 48,
        range: "Range: —",
        source: "IoT: —",
      },
      {
        kind: "metric",
        name: "Feed Rate",
        critical: true,
        should: "0.08 mm/rev",
        actual: "0.081 mm/rev",
        inSpec: true,
        barPct: 54,
        range: "Range: —",
        source: "IoT: —",
      },
      {
        kind: "metric",
        name: "Depth of Cut",
        should: "0.35 mm",
        actual: "0.35 mm",
        inSpec: true,
        barPct: 50,
        range: "Range: —",
        source: "Manual: —",
      },
      {
        kind: "metric",
        name: "Coolant Pressure",
        should: "3.2 bar",
        actual: "3.2 bar",
        inSpec: true,
        barPct: 52,
        range: "Range: —",
        source: "IoT: —",
      },
      {
        kind: "metric",
        name: "Surface Finish Target",
        critical: true,
        should: "Ra 0.8",
        actual: "Ra 0.8",
        inSpec: true,
        barPct: 58,
        range: "Range: —",
        source: "Manual: —",
      },
      {
        kind: "metric",
        name: "Tool Nose Radius",
        should: "0.2 mm",
        actual: "0.2 mm",
        inSpec: true,
        barPct: 50,
        range: "Range: —",
        source: "Manual: —",
      },
    ],
  },
];

function ParamCard({ p }: { p: TwinParam }) {
  if (p.kind === "binary") {
    return (
      <div className={`tv-dt__param ${p.inSpec ? "" : "tv-dt__param--alert"}`}>
        <div className="tv-dt__param-top">
          <h4 className="tv-dt__param-name">{p.name}</h4>
        </div>
        <p className="tv-dt__param-row">
          <strong>Should:</strong> {p.should}
        </p>
        <p className="tv-dt__param-row">
          <strong>Actual:</strong>{" "}
          <span className={p.inSpec ? "tv-dt__param-actual--ok" : "tv-dt__param-actual--bad"}>{p.actual}</span>
        </p>
        {!p.inSpec && p.corrective ? (
          <div className="tv-dt__corrective">
            <strong>Corrective action:</strong> {p.corrective}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="tv-dt__param">
      <div className="tv-dt__param-top">
        <h4 className="tv-dt__param-name">{p.name}</h4>
        {p.critical ? <span className="tv-dt__param-critical">Critical</span> : null}
      </div>
      <p className="tv-dt__param-row">
        <strong>Should:</strong> {p.should}
      </p>
      <p className="tv-dt__param-row">
        <strong>Actual:</strong>{" "}
        <span className={p.inSpec ? "tv-dt__param-actual--ok" : "tv-dt__param-actual--bad"}>{p.actual}</span>
      </p>
      <div className="tv-dt__param-bar">
        <div
          className={`tv-dt__param-bar-fill${p.inSpec ? "" : " tv-dt__param-bar-fill--bad"}`}
          style={{ width: `${p.barPct}%` }}
        />
      </div>
      <div className="tv-dt__param-foot">
        {p.range} · {p.source}
      </div>
    </div>
  );
}

const SPC_BAR_HEIGHTS = [42, 55, 38, 62, 48, 71, 45, 58, 52, 67, 44, 60, 50, 63, 41, 56, 49, 64, 47, 59];

const DigitalTwin = () => {
  const [partId, setPartId] = useState(PART_OPTIONS[0].id);
  const [tab, setTab] = useState<DtTab>("task");
  const [openTaskId, setOpenTaskId] = useState<string | null>("t1");

  const partLabel = useMemo(() => PART_OPTIONS.find((p) => p.id === partId)?.label ?? "—", [partId]);

  const toggleTask = (id: string) => {
    setOpenTaskId((cur) => (cur === id ? null : id));
  };

  return (
    <div className="tv-mes-v2-page">
      <div className="tv-dt">
        <header className="tv-dt__head">
          <div className="tv-dt__titles">
            <p className="tv-dt__sub">
              Real-time parameter monitoring with gap analysis and corrective actions
            </p>
          </div>
          <div className="tv-dt__select-wrap">
            <label htmlFor="tv-dt-part" className="sr-only">
              Part
            </label>
            <select
              id="tv-dt-part"
              className="tv-dt__select"
              value={partId}
              onChange={(e) => setPartId(e.target.value)}
            >
              {PART_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </header>

        <section className="tv-dt__summary" aria-label="Part summary">
          <div className="tv-dt__summary-part">
            <i className="fa fa-line-chart" aria-hidden />
            <span className="tv-dt__summary-part-name">{partLabel}</span>
          </div>
          <div className="tv-dt__summary-metrics">
            <div className="tv-dt__metric">
              <div className="tv-dt__metric-val">2</div>
              <div className="tv-dt__metric-lbl">Tasks with recipe</div>
            </div>
            <div className="tv-dt__metric">
              <div className="tv-dt__metric-val">13</div>
              <div className="tv-dt__metric-lbl">Params monitored</div>
            </div>
            <div className="tv-dt__metric">
              <div className="tv-dt__metric-val tv-dt__metric-val--ok">0</div>
              <div className="tv-dt__metric-lbl">Deviations</div>
            </div>
            <div className="tv-dt__metric">
              <div className="tv-dt__metric-val">100%</div>
              <div className="tv-dt__metric-lbl">Compliance</div>
            </div>
          </div>
        </section>

        <nav className="tv-dt__tabs" aria-label="Digital twin views">
          {(
            [
              ["task", "Task View"],
              ["batch", "Batch View"],
              ["spc", "SPC Charts"],
              ["analytics", "Analytics"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`tv-dt__tab${tab === id ? " tv-dt__tab--active" : ""}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === "task" ? (
          <div className="tv-dt__tasks">
            {TASKS.map((task) => {
              const open = openTaskId === task.id;
              return (
                <section key={task.id} className="tv-dt__task">
                  <button
                    type="button"
                    className="tv-dt__task-head"
                    onClick={() => toggleTask(task.id)}
                    aria-expanded={open}
                  >
                    <div className="tv-dt__task-head-left">
                      <i className="fa fa-check-circle tv-dt__task-ico" aria-hidden />
                      <div className="tv-dt__task-titles">
                        <h2 className="tv-dt__task-name">{task.title}</h2>
                        <p className="tv-dt__task-meta">{task.recipeLine}</p>
                      </div>
                    </div>
                    <div className="tv-dt__task-right">
                      <span className="tv-dt__task-badge">All within spec</span>
                      <i
                        className={`fa fa-chevron-${open ? "up" : "down"} tv-dt__task-chev`}
                        aria-hidden
                      />
                    </div>
                  </button>
                  {open ? (
                    <div className="tv-dt__task-body">
                      {task.note ? <p className="tv-dt__task-note">{task.note}</p> : null}
                      <div className="tv-dt__param-grid">
                        {task.params.map((p, pi) => (
                          <ParamCard key={`${task.id}-${pi}-${p.name}`} p={p} />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        ) : null}

        {tab === "batch" ? (
          <div className="tv-dt__panel">
            <h2 className="tv-dt__panel-title">Batch/Lot Tracking</h2>
            <div className="tv-dt__table-wrap">
              <table className="tv-dt__table">
                <thead>
                  <tr>
                    <th>Batch/Lot ID</th>
                    <th>Quantity</th>
                    <th>Pass count</th>
                    <th>Fail count</th>
                    <th>Yield %</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {BATCH_ROWS.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>{r.qty}</td>
                      <td className="tv-dt__pass">{r.pass}</td>
                      <td className="tv-dt__fail">{r.fail}</td>
                      <td className="tv-dt__yield-cell">
                        <div className="tv-dt__yield-bar">
                          <div className="tv-dt__yield-track">
                            <div className="tv-dt__yield-fill" style={{ width: `${r.yield}%` }} />
                          </div>
                          <span>{r.yield}%</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`tv-dt__pill ${
                            r.status === "released" ? "tv-dt__pill--released" : "tv-dt__pill--review"
                          }`}
                        >
                          {r.status === "released" ? "Released" : "Under Review"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {tab === "spc" ? (
          <div className="tv-dt__panel">
            <h2 className="tv-dt__panel-title">Statistical Process Control</h2>
            <p className="tv-dt__spc-intro">
              Control charts for critical characteristics will bind to historian and inspection
              lots for this part. Below is a layout placeholder for X̄–R style subgroup visualization.
            </p>
            <div className="tv-dt__spc-chart">
              <p className="tv-dt__spc-chart-title">Spindle load — last 20 subgroups</p>
              <div className="tv-dt__spc-bars" role="presentation">
                {SPC_BAR_HEIGHTS.map((h, i) => (
                  <div key={i} className="tv-dt__spc-bar" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="tv-dt__spc-limits">
                <span>UCL (calc)</span>
                <span>Center line</span>
                <span>LCL (calc)</span>
              </div>
            </div>
          </div>
        ) : null}

        {tab === "analytics" ? (
          <div>
            <div className="tv-dt__a-grid4">
              <div className="tv-dt__a-card">
                <i className="fa fa-list-alt" aria-hidden />
                <div>
                  <div className="tv-dt__a-card-val">3</div>
                  <div className="tv-dt__a-card-lbl">Total recipes</div>
                </div>
              </div>
              <div className="tv-dt__a-card">
                <i className="fa fa-check" aria-hidden />
                <div>
                  <div className="tv-dt__a-card-val">2</div>
                  <div className="tv-dt__a-card-lbl">Active recipes</div>
                </div>
              </div>
              <div className="tv-dt__a-card">
                <i className="fa fa-cog" aria-hidden />
                <div>
                  <div className="tv-dt__a-card-val">3/29</div>
                  <div className="tv-dt__a-card-lbl">Mfg technologies</div>
                </div>
              </div>
              <div className="tv-dt__a-card">
                <i className="fa fa-line-chart" aria-hidden />
                <div>
                  <div className="tv-dt__a-card-val">20%</div>
                  <div className="tv-dt__a-card-lbl">Compliance rate</div>
                </div>
              </div>
            </div>

            <div className="tv-dt__a-row2">
              <div className="tv-dt__a-block">
                <h3>Parameter overview</h3>
                <ul className="tv-dt__a-list">
                  <li>
                    Total parameters: <strong>13</strong>
                  </li>
                  <li>
                    Critical parameters: <span className="tv-dt__a-red">10</span>
                  </li>
                  <li>
                    Avg per recipe: <strong>4.3</strong>
                  </li>
                </ul>
              </div>
              <div className="tv-dt__a-block">
                <h3>Production compliance</h3>
                <ul className="tv-dt__a-list">
                  <li>
                    Recipes applied to jobs: <strong>5</strong>
                  </li>
                  <li>
                    Verified (in spec): <span className="tv-dt__a-green">1</span>
                  </li>
                  <li>
                    Deviations: <span className="tv-dt__a-red">2</span>
                  </li>
                  <li>
                    Materials covered: <strong>3/6</strong>
                  </li>
                </ul>
              </div>
            </div>

            <div className="tv-dt__panel" style={{ marginBottom: "1rem" }}>
              <h2 className="tv-dt__panel-title">Mfg. technology coverage</h2>
              <div className="tv-dt__tech-grid">
                {TECH_ITEMS.map((t) => (
                  <div
                    key={t.name}
                    className={`tv-dt__tech ${t.on ? "tv-dt__tech--on" : "tv-dt__tech--dim"}`}
                  >
                    {t.on ? <i className="fa fa-check" aria-hidden /> : null}
                    <span>{t.name}</span>
                    <span className="tv-dt__tech-sub">{t.on ? "Recipe exists" : "No recipe yet"}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="tv-dt__panel">
              <h2 className="tv-dt__panel-title">Deviation analysis</h2>
              <div className="tv-dt__table-wrap">
                <table className="tv-dt__dev-table">
                  <thead>
                    <tr>
                      <th>Task recipe</th>
                      <th>Status</th>
                      <th>Params monitored</th>
                      <th>Out of tolerance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEV_ANALYSIS.map((row) => (
                      <tr key={row.recipe}>
                        <td>{row.recipe}</td>
                        <td>
                          <span className="tv-dt__dev-pill">{row.status}</span>
                        </td>
                        <td>{row.params}</td>
                        <td className="tv-dt__dev-ok">{row.oot}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DigitalTwin;
